import type {
  Companies,
  Customer,
  Employee,
  EmployeeUser,
  LocalPermissions,
  Organization,
  Roles,
  Store,
  Stores,
  User,
  Vehicle,
} from '@/libs/interfaces'

/**
 * Canonical mock dataset shared by every domain's fixtures/handlers, so
 * cross-entity references (orgID, storeID, employeeID, driverID, ...) stay
 * consistent across the whole mocked app.
 */

export const MOCK_ORG_ID = 'org-1'
export const MOCK_COMPANY_ORG_NUMBER = '556677-8899'
export const MOCK_STORE_ID_1 = 'store-1'
export const MOCK_STORE_ID_2 = 'store-2'
export const MOCK_ROLE_ID_ADMIN = 'role-admin'
export const MOCK_ROLE_ID_TECH = 'role-tech'
export const MOCK_USER_ID = 'user-1'
export const MOCK_EMPLOYEE_ID = 'employee-1'

export const mockRoles: Roles[] = [
  {
    roleID: MOCK_ROLE_ID_ADMIN,
    roleName: 'Admin',
    description: 'Full access to all areas',
    orgID: MOCK_ORG_ID,
    createdAt: '2025-01-01T08:00:00Z',
    updatedAt: '2025-01-01T08:00:00Z',
  },
  {
    roleID: MOCK_ROLE_ID_TECH,
    roleName: 'Technician',
    description: 'Workshop floor access',
    orgID: MOCK_ORG_ID,
    createdAt: '2025-01-01T08:00:00Z',
    updatedAt: '2025-01-01T08:00:00Z',
  },
]

/** A single "manage:all" permission grants full CASL access in mock mode. */
export const mockPermissions: LocalPermissions[] = [
  {
    permissionID: 'permission-1',
    permissionTitle: 'manage:all',
    permDispName: 'Manage All',
    description: 'Full access (mock mode)',
    orgID: MOCK_ORG_ID,
    createdAt: '2025-01-01T08:00:00Z',
    updatedAt: '2025-01-01T08:00:00Z',
  },
]

export const mockStoresSimple: Store[] = [
  {
    storeID: MOCK_STORE_ID_1,
    storeName: 'Stockholm City',
    storeOrgNumber: MOCK_COMPANY_ORG_NUMBER,
    storePhone: '+46 8 123 456',
    storeAddress: 'Kungsgatan 12',
    storeEmail: 'stockholm@careofcars.demo',
    orgID: MOCK_ORG_ID,
  },
  {
    storeID: MOCK_STORE_ID_2,
    storeName: 'Göteborg Hisingen',
    storeOrgNumber: MOCK_COMPANY_ORG_NUMBER,
    storePhone: '+46 31 654 321',
    storeAddress: 'Hisingsgatan 45',
    storeEmail: 'goteborg@careofcars.demo',
    orgID: MOCK_ORG_ID,
  },
]

export const mockStores: Stores[] = mockStoresSimple.map((store) => ({
  ...store,
  storeWebSite: 'https://careofcars.demo',
  storeVatNumber: 'SE556677889901',
  storeFSkatt: true,
  storeStatus: 'Active',
  storeZipCode: '111 22',
  storeCity: store.storeName.split(' ')[0],
  storeCountry: 'Sweden',
  storeDescription: 'Demo workshop store',
  storeContactPerson: 'Store Manager',
  storeMaxUsers: 25,
  currency: 'SEK',
  storeAllowCarAPI: true,
  storeAllowSendSMS: true,
  storeSendSMS: true,
  storeUsesCheckin: true,
  storeUsesPIN: true,
  storePaymentOptions: { bankgiro: '123-4567', plusgiro: '98 76 54-3', paymentdays: 30 },
}))

export const mockCompanies: Companies[] = [
  {
    orgID: MOCK_ORG_ID,
    companyOrgNumber: MOCK_COMPANY_ORG_NUMBER,
    companyName: 'Nordic Fleet AB',
    companyReference: 'Erik Johansson',
    companyEmail: 'fleet@nordicfleet.demo',
    companyNumber: 1,
    companyPhone: '+46 8 555 010',
    companyAddress: 'Sveavägen 1',
    companyZipCode: '111 57',
    companyAddressCity: 'Stockholm',
    companyCountry: 'Sweden',
    createdAt: '2025-01-05T08:00:00Z',
    updatedAt: '2025-01-05T08:00:00Z',
  },
]

export const mockEmployees: Employee[] = [
  {
    employeeID: MOCK_EMPLOYEE_ID,
    firstName: 'Anna',
    lastName: 'Karlsson',
    shortUserName: 'AKA',
    userActive: true,
    signature: 'AKA',
    employeeHourlyRate: 350,
    employeeHourlyRateCurrency: 'SEK',
    employeePin: '1234',
    employeeComment: 'Workshop manager',
    orgID: MOCK_ORG_ID,
    email: 'demo@careofcars.demo',
    roleID: MOCK_ROLE_ID_ADMIN,
    employmentNumber: 'EMP-001',
    employeePersonalNumber: '19850101-1234',
    userID: MOCK_USER_ID,
  },
  {
    employeeID: 'employee-2',
    firstName: 'Johan',
    lastName: 'Berg',
    shortUserName: 'JBE',
    userActive: true,
    signature: 'JBE',
    employeeHourlyRate: 280,
    employeeHourlyRateCurrency: 'SEK',
    employeePin: '2345',
    employeeComment: 'Senior technician',
    orgID: MOCK_ORG_ID,
    email: 'johan.berg@careofcars.demo',
    roleID: MOCK_ROLE_ID_TECH,
    employmentNumber: 'EMP-002',
    employeePersonalNumber: '19900212-2345',
    userID: 'user-2',
  },
  {
    employeeID: 'employee-3',
    firstName: 'Maria',
    lastName: 'Lindqvist',
    shortUserName: 'MLI',
    userActive: true,
    signature: 'MLI',
    employeeHourlyRate: 280,
    employeeHourlyRateCurrency: 'SEK',
    employeePin: '3456',
    employeeComment: 'Technician',
    orgID: MOCK_ORG_ID,
    email: 'maria.lindqvist@careofcars.demo',
    roleID: MOCK_ROLE_ID_TECH,
    employmentNumber: 'EMP-003',
    employeePersonalNumber: '19920303-3456',
    userID: 'user-3',
  },
  {
    employeeID: 'employee-4',
    firstName: 'Erik',
    lastName: 'Svensson',
    shortUserName: 'ESV',
    userActive: true,
    signature: 'ESV',
    employeeHourlyRate: 260,
    employeeHourlyRateCurrency: 'SEK',
    employeePin: '4567',
    employeeComment: 'Technician',
    orgID: MOCK_ORG_ID,
    email: 'erik.svensson@careofcars.demo',
    roleID: MOCK_ROLE_ID_TECH,
    employmentNumber: 'EMP-004',
    employeePersonalNumber: '19880404-4567',
    userID: 'user-4',
  },
  {
    employeeID: 'employee-5',
    firstName: 'Sofia',
    lastName: 'Nilsson',
    shortUserName: 'SNI',
    userActive: true,
    signature: 'SNI',
    employeeHourlyRate: 270,
    employeeHourlyRateCurrency: 'SEK',
    employeePin: '5678',
    employeeComment: 'Front desk & technician',
    orgID: MOCK_ORG_ID,
    email: 'sofia.nilsson@careofcars.demo',
    roleID: MOCK_ROLE_ID_TECH,
    employmentNumber: 'EMP-005',
    employeePersonalNumber: '19950505-5678',
    userID: 'user-5',
  },
  {
    employeeID: 'employee-6',
    firstName: 'Oskar',
    lastName: 'Holm',
    shortUserName: 'OHO',
    userActive: false,
    signature: 'OHO',
    employeeHourlyRate: 260,
    employeeHourlyRateCurrency: 'SEK',
    employeePin: '6789',
    employeeComment: 'On leave',
    orgID: MOCK_ORG_ID,
    email: 'oskar.holm@careofcars.demo',
    roleID: MOCK_ROLE_ID_TECH,
    employmentNumber: 'EMP-006',
    employeePersonalNumber: '19870606-6789',
    userID: 'user-6',
  },
]

export const mockEmployeeUser: EmployeeUser = {
  userID: MOCK_USER_ID,
  employeeID: MOCK_EMPLOYEE_ID,
  shortUserName: 'AKA',
  employmentNumber: 'EMP-001',
  employeePersonalNumber: '19850101-1234',
  signature: 'AKA',
  employeeComment: 'Workshop manager',
  employeeHourlyRateCurrency: 'SEK',
  employeeHourlyRate: 350,
}

export const mockOrganization: Organization = {
  isActive: true,
  orgID: MOCK_ORG_ID,
  roleID: MOCK_ROLE_ID_ADMIN,
  roleName: 'Admin',
  orgName: 'Care of Cars Demo',
  userOrgStatus: 'active',
  stores: mockStoresSimple,
  employees: {
    employeeID: mockEmployeeUser.employeeID,
    shortUserName: mockEmployeeUser.shortUserName,
    signature: mockEmployeeUser.signature,
  },
}

export const mockUser: User = {
  userID: MOCK_USER_ID,
  firstName: 'Anna',
  lastName: 'Karlsson',
  email: 'demo@careofcars.demo',
  organizations: [mockOrganization],
}

const swedishVehicleData: Array<
  Pick<Vehicle, 'driverCarBrand' | 'driverCarModel' | 'driverCarColor'>
> = [
  { driverCarBrand: 'Volvo', driverCarModel: 'V60', driverCarColor: 'Black' },
  { driverCarBrand: 'Volvo', driverCarModel: 'XC60', driverCarColor: 'White' },
  { driverCarBrand: 'Saab', driverCarModel: '9-3', driverCarColor: 'Silver' },
  { driverCarBrand: 'Toyota', driverCarModel: 'Corolla', driverCarColor: 'Blue' },
  { driverCarBrand: 'Tesla', driverCarModel: 'Model Y', driverCarColor: 'Red' },
  { driverCarBrand: 'BMW', driverCarModel: '320i', driverCarColor: 'Grey' },
  { driverCarBrand: 'Audi', driverCarModel: 'A4', driverCarColor: 'Black' },
  { driverCarBrand: 'Skoda', driverCarModel: 'Octavia', driverCarColor: 'White' },
  { driverCarBrand: 'Volkswagen', driverCarModel: 'Golf', driverCarColor: 'Blue' },
  { driverCarBrand: 'Polestar', driverCarModel: '2', driverCarColor: 'White' },
]

const swedishCustomerNames: Array<[string, string]> = [
  ['Erik', 'Andersson'],
  ['Lena', 'Pettersson'],
  ['Mikael', 'Olsson'],
  ['Karin', 'Larsson'],
  ['Per', 'Eriksson'],
  ['Eva', 'Gustafsson'],
  ['Anders', 'Johansson'],
  ['Helena', 'Persson'],
  ['Stefan', 'Jonsson'],
  ['Marie', 'Bergström'],
]

export const mockCustomers: Customer[] = swedishCustomerNames.map(
  ([firstName, lastName], index) => ({
    driverID: `customer-${index + 1}`,
    driverFirstName: firstName,
    driverLastName: lastName,
    driverEmail: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
    driverPhoneNumber: `+46 70 ${100000 + index * 1111}`,
    driverAddress: `Storgatan ${index + 1}`,
    driverNo: index + 1,
    driverZipCode: '112 33',
    driverAddressCity: index % 2 === 0 ? 'Stockholm' : 'Göteborg',
    driverCountry: 'Sweden',
    driverStatus: 'Active',
    driverGDPRAccept: true,
    driverAcceptsMarketing: index % 3 === 0,
    driverHasCard: index % 2 === 0,
    orgID: MOCK_ORG_ID,
    createdAt: '2025-02-01T08:00:00Z',
    updatedAt: '2025-02-01T08:00:00Z',
  }),
)

export const mockVehicles: Vehicle[] = swedishVehicleData.map((data, index) => ({
  driverCarID: `vehicle-${index + 1}`,
  driverCarRegistrationNumber: `ABC${100 + index}`,
  driverCarBrand: data.driverCarBrand,
  driverCarModel: data.driverCarModel,
  driverCarColor: data.driverCarColor,
  horsePower: 150 + index * 10,
  gearType: index % 2 === 0 ? 'Automatic' : 'Manual',
  carType: 'Car',
  driverCarMilage: 20000 + index * 5000,
  driverCarRegDate: '2020-06-15',
  fuelType: index % 4 === 0 ? 'Electric' : 'Petrol',
  driverCarYear: 2018 + (index % 6),
  driverCarStatus: 'Active',
  driverID: mockCustomers[index]?.driverID,
  orgID: MOCK_ORG_ID,
  createdAt: '2025-02-01T08:00:00Z',
  updatedAt: '2025-02-01T08:00:00Z',
}))
